import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { ExchangeProtectionWebComponent } from '../component/ExchangeProtectionWebComponent';
import { ExchangeProtectionRepository } from '../repository/ExchangeProtectionRepository';
import { ExchangeProtectionView } from '../view/ExchangeProtectionView';
import { Column, ColumnKeys, Dependencies } from '../types';
import { templateExchangeProtection } from '../view/template';
import { ExchangeProtectionDetailsPanelWebComponent } from '../../exchangeProtectionDetailsPanel';
import { SideMenuEvents, SideMenuWidthValues } from '../../../ui/gzSideMenu/types';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu/component/GzSideMenuWebComponent';
import { GridEvents } from '../../../grid/types/common';
import { GzDialogEvents } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from 'apps/webcomponents/src/ui/gzDialog/component/GzDialogWebComponent';
import { GridViewController } from 'apps/webcomponents/src/grid/gridView/controller/GridViewController';
import { QuarantineActionEnabler } from '../helpers/quarantineActionsEnabler';
import { ExchangeProtectionItem } from '../types/exchangeProtectionResponse';
import { QuarantineToastNotification } from '../../uiElements/notifications/toastNotification';
import { DetailsPanelBuilder } from '../../uiElements/panels/detailsPanelBuilder';
import { DeleteDialog } from '../../uiElements/dialogs/deleteDialog';
import { SaveDialog } from '../../uiElements/dialogs/saveWindow';
import { EmptyQuarantineDialog } from '../../uiElements/dialogs/emptyQuarantineDialog';
import { RestoreWindow } from '../../uiElements/dialogs/restoreWindow';
import {RestoreOriginalDialog} from "../../uiElements/dialogs/restoreOriginalDialog";
import {SelectEvents} from "../../../ui/gzSelect/types";
import {SelectableAction} from "../../computersAndVirtualMachines/types";

export class ExchangeProtectionController extends BaseWebComponentController {
  public component: ExchangeProtectionWebComponent;
  public dataRepository: ExchangeProtectionRepository;
  public view: ExchangeProtectionView;
  public viewConstructor: typeof ExchangeProtectionView;
  public dataRepositoryConstructor: typeof ExchangeProtectionRepository;
  public sideMenu = new GzSideMenuWebComponent();
  public dependencies: Dependencies;

  private actionToMethod = {
    [SelectableAction.RESTORE]: this.openRestoreDialog.bind(this),
    [SelectableAction.RESTORE_ORIGINAL]: this.openRestoreOriginalDialog.bind(this),
  };

  constructor(component: ExchangeProtectionWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ExchangeProtectionView,
      viewTemplate: templateExchangeProtection,
      dataRepositoryConstructor: ExchangeProtectionRepository,
    });
  }

  async getDataForView() {
    return this.dataRepository.getDataForView();
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: e => this.actionSelected(e),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.onRowClick,
      },
      {
        element: this.view.dom.$save,
        event: ButtonEvents.CLICK,
        callback: this.openSaveDialog,
      },
      {
        element: this.view.dom.$emptyQuarantineBtn,
        event: 'click',
        callback: () => this.openEmptyQuarantineDialog(),
      },
      {
        element: this.view.dom.$deleteTaskBtn,
        event: 'click',
        callback: () => this.openDeleteDialog(),
      },
      {
        element: this.sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.closeSideMenu,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: ({ detail: { selectedItemsData } }) => this.enableDisableGridActions(selectedItemsData),
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: ({ detail }) => this.updateEmptyQuarantineButtonState(detail),
      },
    ];
  }

  private actionSelected(event: WebComponentEvent<any>): void {
    const method = this.actionToMethod[event.detail.selected];

    if (method) {
      method();
    }
  }

  private openDeleteDialog(): void {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };
    const hasGhostItems = selectedItems?.some(item => item.isGhost);
    const dialog = DeleteDialog.open(this.dependencies.translateFn, hasGhostItems);

    this.attachListeners(dialog, this.deleteEntries);
  }

  private deleteEntries = async (dialog: GzDialogWebComponent): Promise<void> => {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };

    dialog.close();

    if (records) {
      try {
        const { nbOfCreatedTasks } = await this.dataRepository.deleteSelected({ records });

        if (nbOfCreatedTasks) {
          this.openCreateTaskToast();
        }
      } catch (e) {}

      this.reloadGridItems();
    }
  };

  private restoreOriginalEntries = async (dialog: GzDialogWebComponent): Promise<void> => {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };

    dialog.close();

    if (records) {
      try {
        const { nbOfCreatedTasks } = await this.dataRepository.restoreOriginalSelected({ records });

        if (nbOfCreatedTasks) {
          this.openCreateTaskToast();
        }
      } catch (e) {}

      this.reloadGridItems();
    }
  };

  private openEmptyQuarantineDialog(): void {
    const { translateFn, permissions } = this.dependencies;
    const dialog = EmptyQuarantineDialog.open(translateFn, permissions);

    this.attachListeners(dialog, this.emptyQuarantine);
  }

  private emptyQuarantine = async (dialog: GzDialogWebComponent, { detail }): Promise<void> => {
    dialog.close();

    try {
      const { nbOfCreatedTasks } = await this.dataRepository.emptyQuarantine(detail);

      if (nbOfCreatedTasks) {
        this.openCreateTaskToast();
      }
    } catch (e) {}

    this.reloadGridItems();
  };

  private closeSideMenu() {
    this.sideMenu.closeSideMenu(false);
  }

  private openCreateTaskToast() {
    QuarantineToastNotification.createToastNotification({
      message: this.dependencies.translateFn('Task created.'),
      actionButtonText: this.dependencies.translateFn('View status'),
      hasAction: true,
      actionCallback: this.dependencies.navigateToTasks,
    });
  }

  private openSaveDialog(): void {
    const dialog = SaveDialog.open(this.dependencies.translateFn, this.view.dom.$hostElement);

    this.attachListeners(dialog, this.saveSelectedItems);
  }

  private openRestoreDialog(): void {
    this.dependencies.openExchangeQuarantineRestoreWindow({
      getSelection: () => {
        const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };
        return records;
      },
      reload: () => {
        this.view.dom.$grid.controller?.handleGridItemsLoad(true);
      },
    });
  }

  private openRestoreOriginalDialog(): void {
    const { selectedItems } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };
    const dialog = RestoreOriginalDialog.open(this.dependencies.translateFn);

    this.attachListeners(dialog, this.restoreOriginalEntries);
  }

  private saveSelectedItems = async (dialog: GzDialogWebComponent, { detail }): Promise<void> => {
    const { selectedItems: records } = this.view.dom.$grid as { selectedItems: Array<ExchangeProtectionItem> };

    dialog.close();

    if (records?.length) {
      try {
        await this.dataRepository.saveSelected({
          records,
          settings: {
            path: detail.savePath,
          },
        });

        this.openCreateTaskToast();
      } catch (err) {}

      this.reloadGridItems();
    }
  };

  private enableDisableGridActions(selectedItems: Array<ExchangeProtectionItem>): void {
    QuarantineActionEnabler.handleActionButtons(this.view, selectedItems);
  }

  private attachListeners(dialog: GzDialogWebComponent, confirmCallback: Function) {
    dialog.addEventListener(GzDialogEvents.CONFIRM, event => confirmCallback(dialog, event), { once: true });
    dialog.addEventListener(GzDialogEvents.CANCEL, () => dialog.close(), { once: true });
  }

  private async reloadGridItems(): Promise<void> {
    await (this.view.dom.$grid?.controller as GridViewController<Column, ColumnKeys>)?.handleGridItemsLoad();
  }

  private updateEmptyQuarantineButtonState(detail: any): void {
    const { loadedItems } = detail;

    if (loadedItems) {
      this.view.enableEmptyQuarantine(true);
    }
  }

  private onRowClick(evt): any {
    const isCheckbox = evt.detail.originalEvent.composedPath().find(elem => elem.nodeName === 'VAADIN-CHECKBOX');

    if (evt.detail.rowDetails && !isCheckbox) {
      const panel = DetailsPanelBuilder.buildExchangeProtectionDetailsPanel(
        evt.detail.rowDetails,
        this.dependencies.translateFn
      );
      this.showExchangeProtectionDetailsPanel(panel);
    }
  }

  private showExchangeProtectionDetailsPanel(panel: ExchangeProtectionDetailsPanelWebComponent) {
    let exchangeProtectionShadow = this.view.dom.$hostElement;

    exchangeProtectionShadow.appendChild(this.sideMenu);
    this.sideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
    this.sideMenu.setMenuContent(panel);

    this.sideMenu.openSideMenu();
    this.sideMenu.closeOnClickOutside = true;
  }
}
